import { isApiError } from '@/shared/lib/apiClient';
import type { TFunction } from 'i18next';
import type { AdminTrialItem } from '../types/adminTrial';

export const EXPIRING_SOON_DAYS = 3;

export function getDaysRemaining(trialEndsAt: string | null): number | null {
  if (!trialEndsAt) return null;
  const end = new Date(trialEndsAt);
  const now = new Date();
  const diffMs = end.getTime() - now.getTime();
  return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
}

export function isExpiringSoon(trial: AdminTrialItem): boolean {
  if (trial.subscription_status !== 'trialing') return false;
  const days = trial.days_remaining ?? getDaysRemaining(trial.trial_ends_at);
  return days !== null && days >= 0 && days <= EXPIRING_SOON_DAYS;
}

export function isTrialExpired(trial: AdminTrialItem): boolean {
  if (trial.subscription_status === 'expired') return true;
  if (trial.subscription_status === 'cancelled') return false;

  const days = trial.days_remaining ?? getDaysRemaining(trial.trial_ends_at);
  return days !== null && days < 0;
}

export interface TrialKpis {
  total: number;
  active: number;
  expiringSoon: number;
  expired: number;
  suspended: number;
  cancelled: number;
  avgRemainingDays: number | null;
}

export function computeTrialKpis(trials: AdminTrialItem[]): TrialKpis {
  const activeTrials = trials.filter((t) => t.subscription_status === 'trialing');
  const remainingDays = activeTrials
    .map((t) => t.days_remaining ?? getDaysRemaining(t.trial_ends_at))
    .filter((d): d is number => d !== null && d >= 0);

  return {
    total: trials.length,
    active: activeTrials.length,
    expiringSoon: trials.filter(isExpiringSoon).length,
    expired: trials.filter(isTrialExpired).length,
    suspended: trials.filter((t) => t.subscription_status === 'suspended').length,
    cancelled: trials.filter((t) => t.subscription_status === 'cancelled').length,
    avgRemainingDays:
      remainingDays.length === 0
        ? null
        : Math.round(remainingDays.reduce((a, b) => a + b, 0) / remainingDays.length),
  };
}

export interface TrialActionAvailability {
  extend: boolean;
  suspend: boolean;
  cancel: boolean;
  resume: boolean;
  resumeRequiresExtendFirst: boolean;
}

export function getTrialActionAvailability(trial: AdminTrialItem): TrialActionAvailability {
  const status = trial.subscription_status;

  if (status === 'cancelled') {
    return {
      extend: false,
      suspend: false,
      cancel: false,
      resume: false,
      resumeRequiresExtendFirst: false,
    };
  }

  if (status === 'trialing') {
    return {
      extend: true,
      suspend: true,
      cancel: true,
      resume: false,
      resumeRequiresExtendFirst: false,
    };
  }

  if (status === 'suspended') {
    const days = trial.days_remaining ?? getDaysRemaining(trial.trial_ends_at);
    const trialEnded = days !== null && days < 0;
    return {
      extend: true,
      suspend: false,
      cancel: true,
      resume: !trialEnded,
      resumeRequiresExtendFirst: trialEnded,
    };
  }

  if (status === 'expired') {
    return {
      extend: true,
      suspend: false,
      cancel: true,
      resume: false,
      resumeRequiresExtendFirst: false,
    };
  }

  if (status === 'active') {
    return {
      extend: false,
      suspend: false,
      cancel: false,
      resume: false,
      resumeRequiresExtendFirst: false,
    };
  }

  return {
    extend: true,
    suspend: true,
    cancel: true,
    resume: false,
    resumeRequiresExtendFirst: false,
  };
}

/** Mirrors backend extend base date: max(trial_ends_at, now) + days. */
export function calculateExtendedTrialEnd(trial: AdminTrialItem, days: number): Date | null {
  if (!Number.isFinite(days) || days < 1) return null;
  const now = new Date();
  const currentEnd = trial.trial_ends_at ? new Date(trial.trial_ends_at) : null;
  const base = currentEnd && currentEnd > now ? currentEnd : now;
  const result = new Date(base);
  result.setDate(result.getDate() + days);
  return result;
}

export function formatDate(iso: string | null, locale: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function formatDateTime(iso: string | null, locale: string): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(locale, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function safeDisplay(value: string | null | undefined): string | null {
  if (value == null) return null;
  const trimmed = String(value).trim();
  return trimmed.length > 0 ? trimmed : null;
}

export function formatShortId(value: string | null | undefined, head = 6, tail = 6): string | null {
  const id = safeDisplay(value);
  if (!id) return null;
  if (id.length <= head + tail + 1) return id;
  return `${id.slice(0, head)}…${id.slice(-tail)}`;
}

export async function copyToClipboard(value: string): Promise<boolean> {
  if (!value) return false;
  try {
    await navigator.clipboard.writeText(value);
    return true;
  } catch {
    try {
      const textarea = document.createElement('textarea');
      textarea.value = value;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(textarea);
      return ok;
    } catch {
      return false;
    }
  }
}

export type TrialActionKey = 'extend' | 'suspend' | 'resume' | 'cancel';

export function getTrialActionDisabledReason(
  action: TrialActionKey,
  trial: AdminTrialItem,
  context: {
    identityConfirmed: boolean;
    canUpdate: boolean;
    canSuspend: boolean;
    canCancel: boolean;
  },
  t: TFunction,
): string | null {
  const availability = getTrialActionAvailability(trial);
  const { identityConfirmed, canUpdate, canSuspend, canCancel } = context;

  if (!identityConfirmed) {
    return t('trials.actions.disabled.identityIncomplete');
  }

  switch (action) {
    case 'extend':
      if (!canUpdate) return t('trials.actions.disabled.noUpdatePermission');
      if (!availability.extend) {
        if (trial.subscription_status === 'cancelled') {
          return t('trials.actions.disabled.cancelled');
        }
        if (trial.subscription_status === 'active') {
          return t('trials.actions.disabled.activePaid');
        }
        return t('trials.actions.disabled.notAvailable');
      }
      return null;
    case 'suspend':
      if (!canSuspend) return t('trials.actions.disabled.noSuspendPermission');
      if (!canUpdate) return t('trials.actions.disabled.noUpdatePermission');
      if (!availability.suspend) return t('trials.actions.disabled.notAvailable');
      return null;
    case 'resume':
      if (!canSuspend) return t('trials.actions.disabled.noSuspendPermission');
      if (!canUpdate) return t('trials.actions.disabled.noUpdatePermission');
      if (availability.resumeRequiresExtendFirst) {
        return t('trials.actions.disabled.extendFirst');
      }
      if (!availability.resume) return t('trials.actions.disabled.notAvailable');
      return null;
    case 'cancel':
      if (!canCancel) return t('trials.actions.disabled.noCancelPermission');
      if (!canUpdate) return t('trials.actions.disabled.noUpdatePermission');
      if (!availability.cancel) return t('trials.actions.disabled.notAvailable');
      return null;
    default:
      return null;
  }
}

export function formatTrialApiError(err: unknown, t: TFunction): string {
  if (isApiError(err)) {
    if (err.status === 409) {
      return t('trials.errors.versionConflict');
    }
    if (err.code === 'TRIAL_EXPIRED') {
      return t('trials.errors.trialExpiredResume');
    }
    if (err.code === 'TRIAL_CANCELLED') {
      return t('trials.errors.trialCancelled');
    }
    if (err.code === 'TRIAL_NOT_SUSPENDED') {
      return t('trials.errors.trialNotSuspended');
    }
    if (err.code === 'VALIDATION_ERROR') {
      return t('trials.errors.validation');
    }
    return err.message || t('common.error');
  }
  if (err instanceof Error) {
    return err.message;
  }
  return t('common.error');
}
